import React, { Component } from 'react';
import { Modal } from 'react-bootstrap';
import moment from 'moment';
import { loadGoogleMaps } from '../../../util/googleMapsLoader';
import { isMobileLocation, getLocationBadgeColor, getLocationName, getBaseCoords, isAllLocations } from '../../../util/locationUtils';
import RouteService from '../../../services/routeService';
import { STOP_DISPLAY_STATES, STOP_STATE_VISUAL, deriveStopDisplayState } from '../../../util/routeStopState';

// Admin dispatch map — every mobile crew's route plotted on one map for the
// selected date. Numbered pins per stop, polylines per crew (color-coded),
// base anchor per crew. Click pin → detail drawer. Click polyline → highlight
// that crew (dim others). Click empty map → clear highlight.

const HIGHLIGHT_OPACITY = 0.9;
const DIMMED_OPACITY = 0.2;

// Reuse the per-state SVG marker generator from RouteMapView. Slightly tinted
// border per crew so two crews near the same stop don't collide visually.
// theme-ignore-start — map marker SVG, drawn on map tiles that stay light in both themes
const buildPinSvg = (displayState, sequence, crewColor) => {
  const visual = STOP_STATE_VISUAL[displayState] || STOP_STATE_VISUAL.up_next;
  const stateColor = visual.color;
  const isCompleted = displayState === STOP_DISPLAY_STATES.COMPLETED;
  const label = isCompleted ? '✓' : String(sequence ?? '');
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40">
      <circle cx="20" cy="20" r="17" fill="${crewColor}" opacity="0.18"/>
      <circle cx="20" cy="20" r="14" fill="#ffffff" stroke="${stateColor}" stroke-width="3"/>
      <text x="20" y="25" text-anchor="middle" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif"
            font-size="14" font-weight="700" fill="${stateColor}">${label}</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const buildBaseSvg = (crewColor) => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
      <circle cx="16" cy="16" r="13" fill="${crewColor}"/>
      <text x="16" y="21" text-anchor="middle" font-family="-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif"
            font-size="14" font-weight="900" fill="#ffffff">⌂</text>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};
// theme-ignore-end

class TodayMapView extends Component {
  state = {
    loading: true,
    mapsReady: false,
    routes: [],            // [{ locationId, route, error }]
    selectedStop: null,    // stop in drawer
    highlightedLocationId: null,
    mapError: null,
  };

  mapRef = React.createRef();
  map = null;
  // crews[locationId] = { polylines: DirectionsRenderer[], markers: Marker[], baseMarker, color }
  crews = {};

  async componentDidMount() {
    try {
      const maps = await loadGoogleMaps();
      this.maps = maps;
      this.setState({ mapsReady: true });
      await this.loadRoutes();
      this.initMap();
    } catch (err) {
      this.setState({ mapError: err.message || 'Could not load map.', loading: false });
    }
  }

  componentDidUpdate(prevProps) {
    const dateChanged = prevProps.selectedDate?.format?.('YYYY-MM-DD') !== this.props.selectedDate?.format?.('YYYY-MM-DD');
    const refreshBumped = prevProps.refreshKey !== this.props.refreshKey;
    if (dateChanged || refreshBumped) {
      this.loadRoutes().then(() => this.renderAllCrews());
    }
  }

  componentWillUnmount() {
    this.clearCrews();
  }

  // Mobile crews to plot: every mobile location at "All Locations", else just the
  // selected one. Only has a visible effect when an installer runs 2+ mobile crews;
  // for a single mobile location this returns that same location either way. Location
  // switches full-reload the app, so reading the prop at mount/re-render is sufficient.
  getMobileLocations = () => {
    const mobile = (this.props.locations || []).filter(isMobileLocation);
    const sel = this.props.selectedLocationId;
    if (isAllLocations(sel)) return mobile;
    return mobile.filter(l => String(l.location_id) === String(sel));
  };

  loadRoutes = async () => {
    const { selectedDate } = this.props;
    const mobileLocs = this.getMobileLocations();
    if (mobileLocs.length === 0) {
      this.setState({ routes: [], loading: false });
      return;
    }
    this.setState({ loading: true });
    const dateStr = moment(selectedDate || new Date()).format('YYYY-MM-DD');
    const results = await RouteService.getRoutesForLocations(
      mobileLocs.map(l => l.location_id),
      dateStr
    );
    this.setState({ routes: results, loading: false });
  };

  initMap = () => {
    const maps = this.maps;
    if (!maps || !this.mapRef.current) return;

    const center = this.computeInitialCenter();
    this.map = new maps.Map(this.mapRef.current, {
      center,
      zoom: 10,
      streetViewControl: false,
      fullscreenControl: false,
      mapTypeControl: false,
    });

    // Tap on empty map area clears the highlight.
    this.map.addListener('click', () => {
      if (this.state.highlightedLocationId) {
        this.setState({ highlightedLocationId: null }, this.applyHighlight);
      }
    });

    this.renderAllCrews();
  };

  computeInitialCenter = () => {
    const { routes } = this.state;

    // Prefer the average of all stop coordinates across crews.
    const stopCoords = [];
    routes.forEach(({ route }) => {
      (route?.stops || []).forEach(s => {
        if (s.customer?.lat != null && s.customer?.lng != null) {
          stopCoords.push({ lat: s.customer.lat, lng: s.customer.lng });
        }
      });
    });
    if (stopCoords.length > 0) {
      return {
        lat: stopCoords.reduce((sum, c) => sum + c.lat, 0) / stopCoords.length,
        lng: stopCoords.reduce((sum, c) => sum + c.lng, 0) / stopCoords.length,
      };
    }

    // Fallback: average of mobile-location bases (no jobs scheduled yet).
    const baseCoords = this.getMobileLocations()
      .map(getBaseCoords)
      .filter(Boolean);
    if (baseCoords.length > 0) {
      return {
        lat: baseCoords.reduce((sum, c) => sum + c.lat, 0) / baseCoords.length,
        lng: baseCoords.reduce((sum, c) => sum + c.lng, 0) / baseCoords.length,
      };
    }

    return { lat: 39.5, lng: -98.35 }; // US geographic center fallback
  };

  renderAllCrews = () => {
    if (!this.map || !this.maps) return;
    this.clearCrews();

    const { locations } = this.props;
    const { routes } = this.state;
    const maps = this.maps;
    const bounds = new maps.LatLngBounds();
    let didExtend = false;

    // Iterate over mobile locations rather than over routes — that way base
    // anchors render for crews with no route data yet (e.g. day not scheduled
    // or server route endpoint still 404ing).
    const mobileLocations = this.getMobileLocations();

    mobileLocations.forEach(location => {
      const locationId = location.location_id;
      const color = getLocationBadgeColor(locationId, locations);
      const crew = { markers: [], polylines: [], baseMarker: null, color };
      this.crews[locationId] = crew;

      // Base anchor pin (always, when we have base coords).
      const baseCoords = getBaseCoords(location);
      if (baseCoords) {
        const baseMarker = new maps.Marker({
          position: baseCoords,
          map: this.map,
          icon: { url: buildBaseSvg(color), scaledSize: new maps.Size(32, 32) },
          title: getLocationName(location),
        });
        baseMarker.addListener('click', () => {
          this.setState({ highlightedLocationId: locationId }, this.applyHighlight);
        });
        crew.baseMarker = baseMarker;
        bounds.extend(baseMarker.getPosition());
        didExtend = true;
      }

      // Route data — overlay stops + polyline if we have them.
      const routeEntry = routes.find(r => String(r.locationId) === String(locationId));
      const route = routeEntry?.route;
      const stopsWithCoords = (route?.stops || []).filter(s => s.customer?.lat != null && s.customer?.lng != null);

      stopsWithCoords.forEach(stop => {
        const ds = deriveStopDisplayState(stop.state);
        const marker = new maps.Marker({
          position: { lat: stop.customer.lat, lng: stop.customer.lng },
          map: this.map,
          icon: { url: buildPinSvg(ds, stop.sequence, color), scaledSize: new maps.Size(40, 40) },
          title: `${stop.sequence}. ${stop.customer?.name || ''}`,
        });
        marker.addListener('click', () => this.setState({ selectedStop: { stop, locationId } }));
        crew.markers.push(marker);
        bounds.extend(marker.getPosition());
        didExtend = true;
      });

      // Polyline via Directions API — base → stop 1 → ... → stop N → base
      // when base coords are known. Falls back to stop-1 → stop-N if not.
      if (stopsWithCoords.length >= 1) {
        const directionsService = new maps.DirectionsService();
        const renderer = new maps.DirectionsRenderer({
          map: this.map,
          suppressMarkers: true,
          polylineOptions: {
            strokeColor: color,
            strokeWeight: 4,
            strokeOpacity: HIGHLIGHT_OPACITY,
          },
        });
        crew.polylines.push(renderer);

        let origin, destination, waypoints;
        if (baseCoords) {
          // Base round-trip: depart base → stops → return to base.
          origin = baseCoords;
          destination = baseCoords;
          waypoints = stopsWithCoords.map(s => ({
            location: { lat: s.customer.lat, lng: s.customer.lng },
            stopover: true,
          }));
        } else if (stopsWithCoords.length >= 2) {
          // No base — render just stop-to-stop.
          origin = stopsWithCoords[0].customer;
          destination = stopsWithCoords[stopsWithCoords.length - 1].customer;
          waypoints = stopsWithCoords.slice(1, -1).map(s => ({
            location: { lat: s.customer.lat, lng: s.customer.lng },
            stopover: true,
          }));
        } else {
          // Single stop with no base — skip the polyline entirely.
          return;
        }

        directionsService.route(
          {
            origin,
            destination,
            waypoints,
            travelMode: maps.TravelMode.DRIVING,
            optimizeWaypoints: false,
          },
          (result, status) => {
            if (status === 'OK') renderer.setDirections(result);
          }
        );
      }
    });

    if (didExtend) {
      this.map.fitBounds(bounds, 80);
      // fitBounds on a single point zooms way in — cap it at a sensible level.
      maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
        if (this.map.getZoom() > 13) this.map.setZoom(13);
      });
    }
  };

  applyHighlight = () => {
    const { highlightedLocationId } = this.state;
    Object.entries(this.crews).forEach(([locId, crew]) => {
      const isHighlighted = !highlightedLocationId || String(locId) === String(highlightedLocationId);
      const opacity = isHighlighted ? HIGHLIGHT_OPACITY : DIMMED_OPACITY;
      crew.polylines.forEach(p => {
        p.setOptions({ polylineOptions: { ...p.polylineOptions, strokeOpacity: opacity } });
        // DirectionsRenderer doesn't expose direct polyline ref — re-applying
        // via setOptions is the supported path. Visually subtle but works.
      });
      crew.markers.forEach(m => m.setOpacity(isHighlighted ? 1 : 0.3));
      if (crew.baseMarker) crew.baseMarker.setOpacity(isHighlighted ? 1 : 0.3);
    });
  };

  clearCrews = () => {
    Object.values(this.crews).forEach(crew => {
      crew.markers.forEach(m => m.setMap(null));
      crew.polylines.forEach(p => p.setMap(null));
      if (crew.baseMarker) crew.baseMarker.setMap(null);
    });
    this.crews = {};
  };

  render() {
    const { mapError, loading, routes, selectedStop } = this.state;
    const { locations } = this.props;

    if (mapError) {
      return <div className="alert alert-warning m-3">{mapError}</div>;
    }

    const mobileCount = this.getMobileLocations().length;

    return (
      <div className="today-map-view" style={{ position: 'relative', width: '100%', height: 'calc(100vh - 220px)' }}>
        <div ref={this.mapRef} style={{ width: '100%', height: '100%' }} />

        {loading && (
          <div
            style={{
              position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
              background: 'var(--color-bg-card)', padding: '6px 12px', borderRadius: 999,
              boxShadow: '0 2px 6px rgba(0,0,0,0.15)', fontSize: 13,
            }}
          >
            <i className="fa-sharp fa-solid fa-spinner fa-spin mr-2" />
            Loading crews…
          </div>
        )}

        {!loading && mobileCount === 0 && (
          <div
            style={{
              position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
              background: 'var(--color-bg-card)', padding: 24, borderRadius: 8, textAlign: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            }}
          >
            <i className="fa-sharp fa-regular fa-truck" style={{ fontSize: 32, color: 'var(--color-text-muted)' }} />
            <p className="mt-3 mb-0">No mobile crews configured. Add a mobile location to see them here.</p>
          </div>
        )}

        {/* Crew legend */}
        {!loading && routes.length > 0 && (
          <div
            style={{
              position: 'absolute', top: 12, right: 12, background: 'var(--color-bg-card)',
              padding: 8, borderRadius: 6, boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              fontSize: 12, maxWidth: 200,
            }}
          >
            {routes.map(({ locationId, route }) => {
              const loc = (locations || []).find(l => String(l.location_id) === String(locationId));
              const color = getLocationBadgeColor(locationId, locations);
              const name = loc ? getLocationName(loc) : `Crew ${locationId}`;
              const stops = route?.stops?.length ?? 0;
              return (
                <div
                  key={locationId}
                  className="d-flex align-items-center"
                  style={{ cursor: 'pointer', padding: '2px 0' }}
                  onClick={() => this.setState(
                    { highlightedLocationId: this.state.highlightedLocationId === locationId ? null : locationId },
                    this.applyHighlight
                  )}
                >
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: color, marginRight: 6 }} />
                  <span style={{ flex: 1 }}>{name}</span>
                  <small className="text-muted ml-2">{stops}</small>
                </div>
              );
            })}
          </div>
        )}

        {/* Stop detail drawer */}
        <Modal show={!!selectedStop} onHide={() => this.setState({ selectedStop: null })} centered>
          {selectedStop && (() => {
            const stop = selectedStop.stop;
            const customer = stop.customer || {};
            // Compose vehicle string from whichever shape the server returns —
            // vehicle_label string, flat snake/camelCase fields, or a nested
            // vehicle object. Whichever path wins, missing pieces drop out.
            const v = stop.vehicle || {};
            const vehicleStr = stop.vehicle_label
              || [stop.vehicle_color || v.color,
                  stop.vehicle_year || stop.year || v.year,
                  stop.vehicle_make || stop.make || v.make,
                  stop.vehicle_model || stop.model || v.model,
                  stop.vehicle_trim || stop.trim || v.trim]
                   .filter(Boolean).join(' ');
            const services = stop.services || [];
            return (
              <>
                <Modal.Header closeButton>
                  <Modal.Title>
                    Stop {stop.sequence} · {customer.name || 'Customer'}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  {/* Time + address */}
                  <p className="mb-2">
                    <strong>{stop.start_time} — {stop.end_time}</strong>
                  </p>
                  {customer.address && (
                    <p className="text-muted mb-1">
                      <i className="fa-sharp fa-regular fa-location-dot mr-1" />
                      {customer.address}
                    </p>
                  )}

                  {/* Customer contact */}
                  {customer.phone && (
                    <p className="text-muted mb-3">
                      <i className="fa-sharp fa-regular fa-phone mr-1" />
                      <a href={`tel:${customer.phone}`} className="text-reset">{customer.phone}</a>
                    </p>
                  )}

                  {/* Vehicle */}
                  {vehicleStr && (
                    <p className="mb-3">
                      <i className="fa-sharp fa-regular fa-car mr-1 text-muted" />
                      <strong>{vehicleStr}</strong>
                    </p>
                  )}

                  {/* Services — render category + description per the list-card format */}
                  {services.length > 0 && (
                    <div className="mt-2">
                      {services.map((s, idx) => {
                        const cat = s.category || s.service_category;
                        const desc = s.description || s.custom_description || s.name;
                        const label = [cat, desc].filter(Boolean).join(' - ');
                        return (
                          <div key={idx} className="d-flex align-items-baseline" style={{ gap: 6 }}>
                            <i className="fa-sharp fa-regular fa-wrench text-muted" />
                            <span><strong>{label}</strong></span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Modal.Body>
              </>
            );
          })()}
        </Modal>
      </div>
    );
  }
}

export default TodayMapView;
